import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import _ from 'lodash';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Define the interface for a tasting row
export interface TastingRow {
  'Experiment #'?: number | string;
  Drink: string;
  Date: string;
  Evaluator: string;
  Taste?: number;
  Balance?: number;
  Aroma?: number;
  Presentation?: number;
  Texture?: number;
  'Avg Score'?: number;
  Notes?: string | null;
}

const CocktailDashboard: React.FC = () => {
  // Color scheme
  const COLORS = {
    background: '#FFFFFF', // White background
    secondary: '#011627', // Navy blue
    accent: '#DF2935', // Vibrant red
    tertiary: '#7C9EB2',
    comparison: '#00A86B',
  };

  // Main CSV data, typed with TastingRow[]
  const [data, setData] = useState<TastingRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Additional states
  const [drinks, setDrinks] = useState<string[]>([]);
  const [evaluators, setEvaluators] = useState<string[]>([]);
  const [evaluatorGroups, setEvaluatorGroups] = useState<any[]>([]);
  const [allDrinksByScore, setAllDrinksByScore] = useState<any[]>([]);

  // Filters
  const [selectedDrink, setSelectedDrink] = useState<string>('');
  const [selectedEvaluator, setSelectedEvaluator] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedComparisonEvaluator, setSelectedComparisonEvaluator] =
    useState<string>('');

  // const [xLabels, setXLabels] = useState<string[]>([]);

  // Note rotation
  const [currentNoteIndices, setCurrentNoteIndices] = useState<
    Record<string, number>
  >({});

  // Fetch + parse CSV once on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log(
          'Fetching CSV from path:',
          `${process.env.PUBLIC_URL}/Cocktail Tasting - Experiments-Evaluators.csv`,
        );
        const response = await fetch(
          `${process.env.PUBLIC_URL}/Cocktail Tasting - Experiments-Evaluators.csv`,
        );

        if (!response.ok) {
          console.error(
            'Failed to fetch CSV file:',
            response.status,
            response.statusText,
          );
          throw new Error(
            `Failed to fetch CSV: ${response.status} ${response.statusText}`,
          );
        }

        const text = await response.text();
        console.log('CSV data first 100 chars:', text.substring(0, 100));

        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          delimiter: ',',
          complete: (results) => {
            if (results.errors.length > 0) {
              console.error('Parsing errors:', results.errors);
            }

            // Cast the raw parse results to TastingRow[]
            console.log('Papa Parse results:', results.data);
            const rawData = results.data as TastingRow[];

            // Clean and normalize data
            const cleanedData: TastingRow[] = rawData.map((item) => ({
              ...item,
              Notes:
                item.Notes &&
                item.Notes !== 'null' &&
                item.Notes !== 'No Notes' &&
                item.Notes !== 'No Comment'
                  ? item.Notes
                  : null,
            }));

            setData(cleanedData);

            // Extract unique values for drinks & evaluators
            const uniqueDrinks = [
              ...new Set(cleanedData.map((item) => item.Drink)),
            ].sort();
            const uniqueEvaluators = [
              ...new Set(cleanedData.map((item) => item.Evaluator)),
            ].sort();

            // Calculate all drinks by score for the bar chart
            const drinkScores: Record<
              string,
              { totalScore: number; count: number }
            > = {};
            cleanedData.forEach((item) => {
              if (!drinkScores[item.Drink]) {
                drinkScores[item.Drink] = { totalScore: 0, count: 0 };
              }
              if (
                item['Avg Score'] !== undefined &&
                item['Avg Score'] !== null
              ) {
                drinkScores[item.Drink].totalScore += item['Avg Score'];
                drinkScores[item.Drink].count += 1;
              }
            });

            // Sort by average score
            const sortedDrinks = Object.keys(drinkScores)
              .map((drink) => {
                const { totalScore, count } = drinkScores[drink];
                return {
                  drink,
                  avgScore: count > 0 ? totalScore / count : 0,
                  count,
                };
              })
              .sort((a, b) => b.avgScore - a.avgScore);

            setAllDrinksByScore(sortedDrinks);

            // Create evaluator groups by date
            const groupsByDate: Record<string, Set<string>> = {};
            cleanedData.forEach((row) => {
              if (!groupsByDate[row.Date]) {
                groupsByDate[row.Date] = new Set();
              }
              groupsByDate[row.Date].add(row.Evaluator);
            });

            // Format groups with names instead of just date
            const formattedGroups = Object.entries(groupsByDate)
              .map(([date, evaluatorsSet]) => {
                const evaluatorList = Array.from(evaluatorsSet).sort();
                return {
                  date,
                  evaluators: evaluatorList,
                  label: evaluatorList.join(' / '),
                };
              })
              .sort(
                (a, b) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime(),
              );

            setDrinks(uniqueDrinks);
            setEvaluators(uniqueEvaluators);
            setEvaluatorGroups(formattedGroups);
            setLoading(false);
          },
        });
      } catch (error: unknown) {
        console.error('Error fetching data:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        alert(
          `Failed to load data: ${errorMessage}. Check the console for details.`,
        );
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Reset comparison evaluator when primary evaluator changes
  useEffect(() => {
    if (!selectedEvaluator) {
      setSelectedComparisonEvaluator('');
    }
  }, [selectedEvaluator]);

  // Setup note rotation with memoized notesByDrink to avoid infinite loops
  useEffect(() => {
    // Skip if no data yet
    if (data.length === 0) return;

    // Get all notes for all drinks
    const notesByDrink: Record<
      string,
      { note: string; evaluator: string; drink: string }[]
    > = {};

    data.forEach((item) => {
      if (item.Notes) {
        if (!notesByDrink[item.Drink]) {
          notesByDrink[item.Drink] = [];
        }
        notesByDrink[item.Drink].push({
          note: item.Notes,
          evaluator: item.Evaluator,
          drink: item.Drink,
        });
      }
    });

    // Initialize indices only once when notesByDrink changes
    const initialIndices: Record<string, number> = {};
    Object.keys(notesByDrink).forEach((drink) => {
      initialIndices[drink] = 0;
    });

    setCurrentNoteIndices(initialIndices);

    // Memoize the notesByDrink data for the interval
    const memoizedNotesByDrink = { ...notesByDrink };
  }, [data]); // Only depend on data

  // Helper to check if an evaluator is in a group
  const isEvaluatorInGroup = (evaluatorName: string, groupDate: string) => {
    return data.some(
      (item) => item.Evaluator === evaluatorName && item.Date === groupDate,
    );
  };

  // Helper to check if a drink was evaluated by a group
  const isDrinkInGroup = (drinkName: string, groupDate: string) => {
    return data.some(
      (item) => item.Drink === drinkName && item.Date === groupDate,
    );
  };

  // Helper: get filtered data
  const getFilteredData = () => {
    return data.filter((item) => {
      return (
        (selectedDrink === '' || item.Drink === selectedDrink) &&
        (selectedEvaluator === '' || item.Evaluator === selectedEvaluator) &&
        (selectedGroup === '' || item.Date === selectedGroup)
      );
    });
  };

  const getFilteredOptions = () => {
    let filteredForDrinks = data;
    let filteredForEvaluators = data;
    let filteredForComparisonEvaluators: TastingRow[] = [];

    // 1. If a group is selected, all filters are scoped to that group
    if (selectedGroup) {
      filteredForDrinks = filteredForEvaluators = data.filter(
        (item) => item.Date === selectedGroup,
      );

      // Further narrow if evaluator is selected
      if (selectedEvaluator) {
        filteredForDrinks = filteredForDrinks.filter(
          (item) => item.Evaluator === selectedEvaluator,
        );

        // Get evaluators who have common drinks with the selected evaluator
        const primaryEvaluatorDrinks = [
          ...new Set(
            data
              .filter(
                (item) =>
                  item.Evaluator === selectedEvaluator &&
                  (selectedGroup === '' || item.Date === selectedGroup),
              )
              .map((item) => item.Drink),
          ),
        ];

        // Filter evaluators who have rated at least one common drink
        filteredForComparisonEvaluators = data.filter(
          (item) =>
            item.Evaluator !== selectedEvaluator &&
            primaryEvaluatorDrinks.includes(item.Drink) &&
            (selectedGroup === '' || item.Date === selectedGroup),
        );
      }

      // Further narrow if drink is selected
      if (selectedDrink) {
        filteredForEvaluators = filteredForEvaluators.filter(
          (item) => item.Drink === selectedDrink,
        );
      }
    } else {
      // No group selected

      // If both evaluator and drink are selected
      if (selectedEvaluator && selectedDrink) {
        // Get drinks the evaluator has evaluated
        filteredForDrinks = data.filter(
          (item) => item.Evaluator === selectedEvaluator,
        );

        // Get evaluators who have rated the drink
        filteredForEvaluators = data.filter(
          (item) => item.Drink === selectedDrink,
        );
        // FIX: Get other evaluators who have rated this drink
        filteredForComparisonEvaluators = data.filter(
          (item) =>
            item.Drink === selectedDrink &&
            item.Evaluator !== selectedEvaluator,
        );
      } else if (selectedEvaluator) {
        filteredForDrinks = data.filter(
          (item) => item.Evaluator === selectedEvaluator,
        );

        // Get evaluators who have common drinks with the selected evaluator
        const primaryEvaluatorDrinks = [
          ...new Set(
            data
              .filter((item) => item.Evaluator === selectedEvaluator)
              .map((item) => item.Drink),
          ),
        ];

        // Filter evaluators who have rated at least one common drink
        filteredForComparisonEvaluators = data.filter(
          (item) =>
            item.Evaluator !== selectedEvaluator &&
            primaryEvaluatorDrinks.includes(item.Drink),
        );
      } else if (selectedDrink) {
        filteredForEvaluators = data.filter(
          (item) => item.Drink === selectedDrink,
        );
      }
    }

    const drinkOptions = [
      ...new Set(filteredForDrinks.map((item) => item.Drink)),
    ].sort();

    const evaluatorOptions = [
      ...new Set(filteredForEvaluators.map((item) => item.Evaluator)),
    ].sort();

    const comparisonEvaluatorOptions = [
      ...new Set(filteredForComparisonEvaluators.map((item) => item.Evaluator)),
    ].sort();

    return { drinkOptions, evaluatorOptions, comparisonEvaluatorOptions };
  };

  // Notes for a specific drink
  const getNotesForDrink = (drinkName: string) => {
    return data
      .filter((item) => item.Drink === drinkName && item.Notes)
      .map((item) => ({
        note: item.Notes!,
        evaluator: item.Evaluator,
        drink: item.Drink,
      }));
  };

  // Top 5
  const getTop5Drinks = () => {
    return allDrinksByScore.slice(0, 5);
  };

  // Calculate average scores for a drink, optionally filtered by groupDate
  const getDrinkCriteriaAverages = (
    drinkName: string,
    groupDate: string | null = null,
  ) => {
    // MODIFIED: Don't filter by group date for calculation of averages
    const drinkData = data.filter((item) => item.Drink === drinkName);

    if (drinkData.length === 0) {
      return null;
    }

    const criteriaSum = {
      Taste: 0,
      Balance: 0,
      Aroma: 0,
      Presentation: 0,
      Texture: 0,
      count: 0,
    };

    drinkData.forEach((item) => {
      criteriaSum.Taste += item.Taste || 0;
      criteriaSum.Balance += item.Balance || 0;
      criteriaSum.Aroma += item.Aroma || 0;
      criteriaSum.Presentation += item.Presentation || 0;
      criteriaSum.Texture += item.Texture || 0;
      criteriaSum.count += 1;
    });

    return {
      taste: criteriaSum.count > 0 ? criteriaSum.Taste / criteriaSum.count : 0,
      balance:
        criteriaSum.count > 0 ? criteriaSum.Balance / criteriaSum.count : 0,
      aroma: criteriaSum.count > 0 ? criteriaSum.Aroma / criteriaSum.count : 0,
      presentation:
        criteriaSum.count > 0
          ? criteriaSum.Presentation / criteriaSum.count
          : 0,
      texture:
        criteriaSum.count > 0 ? criteriaSum.Texture / criteriaSum.count : 0,
      avgScore:
        drinkData.reduce((sum, row) => sum + (row['Avg Score'] || 0), 0) /
        drinkData.length,
    };
  };

  // Single evaluator's ratings for a drink
  const getEvaluatorRating = (
    drinkName: string,
    evaluatorName: string,
    groupDate: string | null = null,
  ) => {
    // MODIFIED: Don't filter by group date for calculation of ratings
    const ratings = data.filter(
      (item) => item.Drink === drinkName && item.Evaluator === evaluatorName,
    );

    if (ratings.length === 0) return null;

    const rating = ratings[ratings.length - 1];
    return {
      taste: rating.Taste || 0,
      balance: rating.Balance || 0,
      aroma: rating.Aroma || 0,
      presentation: rating.Presentation || 0,
      texture: rating.Texture || 0,
      avgScore: rating['Avg Score'] || 0,
    };
  };

  // Drinks by evaluator
  const getDrinksByEvaluator = (
    evaluatorName: string,
    groupDate: string | null = null,
  ) => {
    return [
      ...new Set(
        data
          .filter(
            (item) =>
              item.Evaluator === evaluatorName &&
              (groupDate === null || item.Date === groupDate),
          )
          .map((item) => item.Drink),
      ),
    ].sort();
  };

  // Get drinks evaluated by a group
  const getDrinksByGroup = (groupDate: string) => {
    return [
      ...new Set(
        data
          .filter((item) => item.Date === groupDate)
          .map((item) => item.Drink),
      ),
    ].sort();
  };

  // Get drinks evaluated by both evaluators
  const getCommonDrinks = (
    evaluator1: string,
    evaluator2: string,
    groupDate: string | null = null,
  ) => {
    const drinks1 = new Set(
      data
        .filter(
          (item) =>
            item.Evaluator === evaluator1 &&
            (groupDate === null || item.Date === groupDate),
        )
        .map((item) => item.Drink),
    );

    const drinks2 = data
      .filter(
        (item) =>
          item.Evaluator === evaluator2 &&
          (groupDate === null || item.Date === groupDate),
      )
      .map((item) => item.Drink);

    return drinks2.filter((drink) => drinks1.has(drink)).sort();
  };
  // Image component definition
  const DrinkImage: React.FC<{ drinkName: string }> = React.memo(
    ({ drinkName }) => {
      const imageFilename = useMemo(
        () => drinkName.replace(/\s+/g, '-').toLowerCase(),
        [drinkName],
      );
      const imagePath = useMemo(
        () => `${process.env.PUBLIC_URL}/drinks-photos/${imageFilename}.jpg`,
        [imageFilename],
      );

      const [loaded, setLoaded] = useState(true); // assume true by default to prevent flashing

      return (
        <div className="flex justify-center my-3">
          {loaded && (
            <img
              src={imagePath}
              alt={drinkName}
              onError={() => setLoaded(false)}
              className="rounded-md object-cover shadow-sm"
              style={{
                height: '220px',
                width: '220px',
                border: `1px solid #01162720`,
              }}
            />
          )}
        </div>
      );
    },
  );

  // Properly implemented PentagonChart with no conditional hooks
  const PentagonChart: React.FC<{
    criteria: any;
    comparisonCriteria?: any;
    comparisonEvaluatorCriteria?: any;
    drinkName: string;
    isEvaluator?: boolean;
    showLegend?: boolean;
  }> = ({
    criteria,
    comparisonCriteria = null,
    comparisonEvaluatorCriteria = null,
    drinkName,
    isEvaluator = false,
    showLegend = true,
  }) => {
    // IMPORTANT: Place ALL hooks at the top level, before any conditionals

    // Early check for null criteria - but don't return yet
    const hasCriteria = !!criteria;

    // Prepare values that depend on criteria - with safety checks
    const size = 260;
    const padding = 39;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size * 0.5;

    const notesForDrink = getNotesForDrink(drinkName);

    const getPoint = (index: number, r: number) => {
      const angle = (Math.PI * 2 * (index - 1.25)) / 5;
      return {
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle),
      };
    };

    // State for showing which arrow overlay (left/right/none)
    const [pressedSide, setPressedSide] = useState<'left' | 'right' | null>(
      null,
    );

    const handleNoteNav = (direction: 'left' | 'right') => {
      if (!notesForDrink.length) return;
      setCurrentNoteIndices((prev) => {
        const currIdx = prev[drinkName] || 0;
        let newIdx;
        if (direction === 'left') {
          newIdx = (currIdx - 1 + notesForDrink.length) % notesForDrink.length;
        } else {
          newIdx = (currIdx + 1) % notesForDrink.length;
        }
        return { ...prev, [drinkName]: newIdx };
      });
    };

    // Mouse/touch handlers for feedback and navigation
    const handlePointerDown = (side: 'left' | 'right') => setPressedSide(side);
    const handlePointerUp = (side: 'left' | 'right') => {
      handleNoteNav(side);
      setPressedSide(null);
    };
    const handlePointerLeave = () => setPressedSide(null);

    // Keyboard support (optional)
    useEffect(() => {
      const handler = (e: KeyboardEvent) => {
        if (!notesForDrink.length) return;
        if (e.key === 'ArrowLeft') handleNoteNav('left');
        if (e.key === 'ArrowRight') handleNoteNav('right');
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [notesForDrink.length]);

    // Outer pentagon
    const outerPoints = [];
    for (let i = 0; i < 5; i++) {
      outerPoints.push(getPoint(i, radius));
    }

    // Main (inner) pentagon - with null safety
    const criteriaValues = hasCriteria
      ? [
          (criteria.taste || 0) / 10,
          (criteria.balance || 0) / 10,
          (criteria.aroma || 0) / 10,
          (criteria.presentation || 0) / 10,
          (criteria.texture || 0) / 10,
        ]
      : [0, 0, 0, 0, 0]; // Default to zeros if no criteria

    const innerPoints = criteriaValues.map((value, i) =>
      getPoint(i, radius * value),
    );

    // Comparison (average) pentagon
    let comparisonPoints: { x: number; y: number }[] = [];
    if (comparisonCriteria) {
      const comparisonValues = [
        (comparisonCriteria.taste || 0) / 10,
        (comparisonCriteria.balance || 0) / 10,
        (comparisonCriteria.aroma || 0) / 10,
        (comparisonCriteria.presentation || 0) / 10,
        (comparisonCriteria.texture || 0) / 10,
      ];
      comparisonPoints = comparisonValues.map((val, i) =>
        getPoint(i, radius * val),
      );
    }

    // Comparison Evaluator pentagon
    let comparisonEvaluatorPoints: { x: number; y: number }[] = [];
    if (comparisonEvaluatorCriteria) {
      const comparisonEvaluatorValues = [
        (comparisonEvaluatorCriteria.taste || 0) / 10,
        (comparisonEvaluatorCriteria.balance || 0) / 10,
        (comparisonEvaluatorCriteria.aroma || 0) / 10,
        (comparisonEvaluatorCriteria.presentation || 0) / 10,
        (comparisonEvaluatorCriteria.texture || 0) / 10,
      ];
      comparisonEvaluatorPoints = comparisonEvaluatorValues.map((val, i) =>
        getPoint(i, radius * val),
      );
    }

    // Build path strings
    const outerPath =
      outerPoints
        .map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ',' + p.y)
        .join(' ') + 'Z';
    const innerPath =
      innerPoints
        .map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ',' + p.y)
        .join(' ') + 'Z';
    const comparisonPath =
      comparisonPoints.length > 0
        ? comparisonPoints
            .map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ',' + p.y)
            .join(' ') + 'Z'
        : '';
    const comparisonEvaluatorPath =
      comparisonEvaluatorPoints.length > 0
        ? comparisonEvaluatorPoints
            .map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ',' + p.y)
            .join(' ') + 'Z'
        : '';

    const spokeLines = outerPoints.map(
      (p) => `M${centerX},${centerY} L${p.x},${p.y}`,
    );

    // Notes logic - also memoized to prevent unnecessary re-renders
    const currentNoteIndex = currentNoteIndices[drinkName] || 0;
    const currentNote =
      notesForDrink.length > 0 ? notesForDrink[currentNoteIndex] : null;

    // Colors
    const mainColor = isEvaluator ? COLORS.accent : COLORS.tertiary;
    const comparisonColor = COLORS.tertiary;
    const comparisonEvaluatorColor = COLORS.comparison;

    // For touch swipe (add to component):
    const [touchStartX, setTouchStartX] = useState<number | null>(null);

    // 2. PentagonSvg hook - Always define this
    const PentagonSvg = useMemo(() => {
      // Don't render the actual pentagon if we don't have criteria
      if (!hasCriteria) return null;

      return (
        <svg
          width={size}
          height={size}
          viewBox={`-${padding} -${padding} ${size + padding * 2} ${
            size + padding * 2
          }`}
        >
          {/* Axis lines */}
          {spokeLines.map((line, i) => (
            <path
              key={`spoke-${i}`}
              d={line}
              stroke={COLORS.secondary}
              strokeOpacity={0.3}
              strokeWidth="1"
            />
          ))}

          {/* Outer pentagon */}
          <path
            d={outerPath}
            fill="none"
            stroke={COLORS.secondary}
            strokeWidth="1.5"
          />

          {/* Comparison pentagon */}
          {comparisonCriteria && (
            <path
              d={comparisonPath}
              fill="none"
              stroke={comparisonColor}
              strokeWidth="1.5"
            />
          )}

          {/* Comparison Evaluator pentagon */}
          {comparisonEvaluatorCriteria && (
            <path
              d={comparisonEvaluatorPath}
              fill="none"
              stroke={comparisonEvaluatorColor}
              strokeWidth="1.5"
              strokeDasharray="4"
            />
          )}

          {/* Inner pentagon */}
          <path
            d={innerPath}
            fill={`${mainColor}0A`}
            stroke={mainColor}
            strokeWidth="1.5"
            strokeDasharray={isEvaluator ? '4' : '0'}
          />

          {/* Comparison Evaluator score - put it on top */}
          {comparisonEvaluatorCriteria && (
            <text
              x={centerX}
              y={centerY - 14}
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fill={comparisonEvaluatorColor}
            >
              {(comparisonEvaluatorCriteria.avgScore || 0).toFixed(1)}
            </text>
          )}

          {/* Score in center */}
          <text
            x={centerX}
            y={centerY + 2}
            textAnchor="middle"
            fontSize="16"
            fontWeight="bold"
            fill={mainColor}
          >
            {(criteria.avgScore || 0).toFixed(1)}
          </text>

          {/* Comparison score */}
          {comparisonCriteria && (
            <text
              x={centerX}
              y={centerY + 18}
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fill={comparisonColor}
            >
              {(comparisonCriteria.avgScore || 0).toFixed(1)}
            </text>
          )}

          {/* Criteria labels aligned to pentagon points with fine-tuned offsets */}
          {['Taste', 'Balance', 'Aroma', 'Presentation', 'Texture'].map(
            (label, i) => {
              const point = getPoint(i, radius + 15);

              // Fine-tuned x-offsets for alignment
              const xAdjustments = {
                Taste: 0, // top center
                Balance: -14, // bottom right
                Aroma: -10, // bottom left
                Presentation: 20, // upper left (longer word)
                Texture: 15, // upper right
              };

              const adjustedX =
                point.x +
                (xAdjustments[label as keyof typeof xAdjustments] || 0);

              // Determine textAnchor more reliably
              const textAnchor =
                label === 'Taste'
                  ? 'middle'
                  : label === 'Balance' || label === 'Aroma'
                  ? 'start'
                  : label === 'Texture' || label === 'Presentation'
                  ? 'end'
                  : 'middle';

              return (
                <text
                  key={label}
                  x={adjustedX}
                  y={point.y}
                  textAnchor={textAnchor}
                  fontSize="12"
                  fill={COLORS.secondary}
                >
                  {label}
                </text>
              );
            },
          )}
        </svg>
      );
    }, [
      hasCriteria,
      criteria,
      comparisonCriteria,
      comparisonEvaluatorCriteria,
      isEvaluator,
      innerPath,
      outerPath,
      comparisonPath,
      comparisonEvaluatorPath,
      spokeLines,
      size,
      padding,
      centerX,
      centerY,
      mainColor,
      comparisonColor,
      comparisonEvaluatorColor,
    ]);

    // 3. NotesComponent hook - Always define this
    const NotesComponent = useMemo(() => {
      if (!currentNote) return null;

      // Arrow overlay icons (replace with SVG or emojis for now)
      const Arrow = ({ dir }: { dir: 'left' | 'right' }) => (
        <div
          className="absolute top-1/2"
          style={{
            [dir === 'left' ? 'left' : 'right']: '12px',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            opacity: pressedSide === dir ? 1 : 0.2,
            transition: 'opacity 0.2s',
            fontSize: 32,
            color: '#bbb',
            zIndex: 10,
            userSelect: 'none',
          }}
        >
          {dir === 'left' ? '◀' : '▶'}
        </div>
      );

      // Navigation area styles
      const navAreaBase = {
        position: 'absolute' as const,
        top: 0,
        bottom: 0,
        width: '40%',
        zIndex: 5,
        cursor: 'pointer',
        background: pressedSide ? 'rgba(200,200,200,0.08)' : 'transparent',
        transition: 'background 0.15s',
      };

      // In NotesComponent

      return (
        <div
          className="mt-2 p-3 rounded-lg shadow-md text-sm relative border border-gray-200 overflow-hidden"
          style={{
            backgroundColor: COLORS.background,
            height: 110, // keep the card size fixed
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            userSelect: 'none',
            touchAction: 'pan-y',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Main note content (fills most of the card) */}
          <div
            className="flex-grow overflow-y-auto pr-1"
            style={{
              minHeight: 0,
              zIndex: 1,
              marginBottom: 0,
              // Reduce bottom margin to maximize note area
            }}
          >
            <p
              className="italic"
              style={{
                color: COLORS.secondary,
                fontSize:
                  currentNote.note.length > 100 ? '0.75rem' : '0.875rem',
                lineHeight: '1.4',
                minHeight: 36,
                paddingRight: 4,
                marginBottom: 0,
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
              }}
            >
              "{currentNote.note}"
            </p>
          </div>

          {/* Ultra-compact nav row flush bottom */}
          <div
            className="flex items-center justify-between"
            style={{
              marginTop: 4,
              marginBottom: -6, // move row flush to bottom edge
              minHeight: 24, // shrinks the row as much as possible
            }}
          >
            <div className="flex items-center space-x-1">
              {/* Left arrow */}
              {notesForDrink.length > 1 && (
                <button
                  className="p-0 m-0"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    width: 20,
                    height: 20,
                    lineHeight: '20px',
                    padding: 0,
                  }}
                  tabIndex={-1}
                  onClick={() => handleNoteNav('left')}
                  aria-label="Previous note"
                >
                  <span style={{ fontSize: 18, color: '#bbb', opacity: 0.85 }}>
                    ◀
                  </span>
                </button>
              )}
              {/* Right arrow */}
              {notesForDrink.length > 1 && (
                <button
                  className="p-0 m-0"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    width: 20,
                    height: 20,
                    lineHeight: '20px',
                    padding: 0,
                  }}
                  tabIndex={-1}
                  onClick={() => handleNoteNav('right')}
                  aria-label="Next note"
                >
                  <span style={{ fontSize: 18, color: '#bbb', opacity: 0.85 }}>
                    ▶
                  </span>
                </button>
              )}
              {/* Note count */}
              {notesForDrink.length > 1 && (
                <span
                  className="text-xs ml-1"
                  style={{
                    color: COLORS.secondary,
                    minWidth: 32,
                  }}
                >
                  {currentNoteIndex + 1}/{notesForDrink.length}
                </span>
              )}
            </div>
            {/* Evaluator name (right) */}
            <span
              className="text-right font-medium text-xs"
              style={{
                color: COLORS.secondary,
                marginLeft: 6,
                whiteSpace: 'nowrap',
              }}
            >
              - {currentNote.evaluator}
            </span>
          </div>
        </div>
      );

      function handleTouchStart(e: React.TouchEvent) {
        setTouchStartX(e.touches[0].clientX);
      }
      function handleTouchEnd(e: React.TouchEvent) {
        if (touchStartX == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 30) {
          if (dx > 0) handleNoteNav('left');
          else handleNoteNav('right');
        }
        setTouchStartX(null);
      }
    }, [
      currentNote,
      currentNoteIndex,
      notesForDrink,
      pressedSide,
      handlePointerDown,
      handlePointerUp,
      handlePointerLeave,
      COLORS.secondary,
      COLORS.background,
    ]);

    // 4. LegendComponent hook - Always define this
    const LegendComponent = useMemo(() => {
      if (!(comparisonCriteria || comparisonEvaluatorCriteria) || !showLegend) {
        return null;
      }

      return (
        <div className="flex justify-center mt-2 space-x-6">
          <div className="flex items-center">
            <div
              className="w-4 h-1 border-t-2 border-dashed mr-1"
              style={{
                borderColor: isEvaluator ? COLORS.accent : COLORS.tertiary,
              }}
            ></div>
            <span
              className="text-xs"
              style={{ color: isEvaluator ? COLORS.accent : COLORS.tertiary }}
            >
              {isEvaluator ? selectedEvaluator : 'Individual'}
            </span>
          </div>
          {comparisonEvaluatorCriteria && (
            <div className="flex items-center">
              <div
                className="w-4 h-1 border-t-2 border-dashed mr-1"
                style={{ borderColor: COLORS.comparison }}
              ></div>
              <span className="text-xs" style={{ color: COLORS.comparison }}>
                {selectedComparisonEvaluator}
              </span>
            </div>
          )}
          <div className="flex items-center">
            <div
              className="w-4 h-1 mr-1"
              style={{ backgroundColor: COLORS.tertiary }}
            ></div>
            <span className="text-xs" style={{ color: COLORS.tertiary }}>
              Average
            </span>
          </div>
        </div>
      );
    }, [
      comparisonCriteria,
      comparisonEvaluatorCriteria,
      showLegend,
      isEvaluator,
    ]);
    const MemoizedDrinkImage = useMemo(() => {
      return <DrinkImage drinkName={drinkName} />;
    }, [drinkName]);

    // Now decide what to render based on whether we have criteria
    if (!hasCriteria) {
      return (
        <div className="text-center p-4" style={{ color: COLORS.secondary }}>
          No data available for {drinkName}.
        </div>
      );
    }

    // Normal rendering when we have criteria
    return (
      <div className="relative w-full">
        <div
          className="flex items-center justify-center"
          style={{ backgroundColor: COLORS.background }}
        >
          {PentagonSvg}
        </div>

        {/* Use the memoized DrinkImage component */}
        {MemoizedDrinkImage}

        {/* Use the memoized legend */}
        {LegendComponent}

        {/* Use the memoized notes */}
        {NotesComponent}
      </div>
    );
  };

  // Bar Chart
  const ScoreBarChart: React.FC = () => {
    const maxWidth = 100;

    // Filtered evaluator data
    const evaluatorData = selectedEvaluator
      ? data.filter(
          (item) =>
            item.Evaluator === selectedEvaluator &&
            (selectedGroup === '' || item.Date === selectedGroup),
        )
      : [];
    const evaluatorDrinks = [
      ...new Set(evaluatorData.map((item) => item.Drink)),
    ];

    // Filtered comparison evaluator data
    const comparisonEvaluatorData = selectedComparisonEvaluator
      ? data.filter(
          (item) =>
            item.Evaluator === selectedComparisonEvaluator &&
            (selectedGroup === '' || item.Date === selectedGroup),
        )
      : [];
    const comparisonEvaluatorDrinks = [
      ...new Set(comparisonEvaluatorData.map((item) => item.Drink)),
    ];

    // Group drinks
    const groupDrinks = selectedGroup ? getDrinksByGroup(selectedGroup) : [];

    // Determine which drinks to show
    const drinksToShow = allDrinksByScore; // always show all drinks

    if (drinksToShow.length === 0) {
      return (
        <div className="mt-8">
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: COLORS.secondary }}
          >
            All Drinks Ranked by Average Score
          </h2>
          <div
            className="p-4 rounded-lg shadow-md border border-gray-200 text-center"
            style={{
              backgroundColor: COLORS.background,
              color: COLORS.secondary,
            }}
          >
            No drinks match the current filter criteria.
          </div>
        </div>
      );
    }

    return (
      <div className="mt-8">
        <h2
          className="text-xl font-semibold mb-4"
          style={{ color: COLORS.tertiary }}
        >
          All Drinks Ranked by Average Score
        </h2>
        <div
          className="p-4 rounded-lg shadow-md border border-gray-200"
          style={{ backgroundColor: COLORS.background }}
        >
          <div className="flex border-b border-gray-300 pb-2 mb-2">
            <div
              className="w-1/3 font-medium"
              style={{ color: COLORS.secondary }}
            >
              Drink
            </div>
            <div
              className="w-2/3 font-medium"
              style={{ color: COLORS.secondary }}
            >
              Average Score
            </div>
          </div>
          <div className="space-y-1 pr-2">
            {drinksToShow.map((item) => {
              const isSelectedDrink = item.drink === selectedDrink;
              const isEvaluatedBySelected = evaluatorDrinks.includes(
                item.drink,
              );
              const isEvaluatedByGroup = groupDrinks.includes(item.drink);
              const barWidth = (item.avgScore / 10) * maxWidth;

              let evaluatorScore: number | null = null;
              let comparisonEvaluatorScore: number | null = null;

              if (selectedEvaluator && isEvaluatedBySelected) {
                const evItem = [...evaluatorData]
                  .filter((d) => d.Drink === item.drink)
                  .at(-1);
                if (evItem) evaluatorScore = evItem['Avg Score'] || null;
              }

              // Add comparison evaluator's score if applicable
              if (
                selectedComparisonEvaluator &&
                comparisonEvaluatorDrinks.includes(item.drink)
              ) {
                const compEvItem = [...comparisonEvaluatorData]
                  .filter((d) => d.Drink === item.drink)
                  .at(-1);
                if (compEvItem)
                  comparisonEvaluatorScore = compEvItem['Avg Score'] || null;
              }

              // highlight row - MODIFIED to include group highlighting
              const shouldHighlight =
                isSelectedDrink ||
                (selectedEvaluator && isEvaluatedBySelected) ||
                (selectedGroup && isEvaluatedByGroup);

              return (
                <div
                  key={item.drink}
                  className="flex items-center py-1"
                  style={{
                    backgroundColor: shouldHighlight
                      ? `${COLORS.secondary}11`
                      : '',
                  }}
                >
                  <div
                    className="w-1/3 truncate pr-2"
                    title={item.drink}
                    style={{ color: COLORS.secondary }}
                  >
                    {item.drink}
                  </div>
                  <div className="w-2/3 flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-5 relative">
                      {/* Average score bar */}
                      <div
                        className="h-5 rounded-full"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: shouldHighlight
                            ? COLORS.secondary
                            : '#94a3b8',
                        }}
                      ></div>

                      {/* Evaluator score marker */}
                      {evaluatorScore !== null && (
                        <div
                          className="absolute top-0 w-1 h-5"
                          style={{
                            left: `${(evaluatorScore / 10) * maxWidth}%`,
                            backgroundColor: COLORS.accent,
                          }}
                        ></div>
                      )}

                      {/* Comparison Evaluator score marker */}
                      {comparisonEvaluatorScore !== null && (
                        <div
                          className="absolute top-0 w-1 h-5"
                          style={{
                            left: `${
                              (comparisonEvaluatorScore / 10) * maxWidth
                            }%`,
                            backgroundColor: COLORS.comparison,
                          }}
                        ></div>
                      )}

                      {/* Score text */}
                      <div className="absolute inset-0 flex items-center pl-2 text-xs text-white font-medium">
                        {item.avgScore.toFixed(1)}
                        {evaluatorScore !== null && (
                          <span
                            className="ml-2 font-bold"
                            style={{ color: COLORS.accent }}
                          >
                            ({evaluatorScore.toFixed(1)})
                          </span>
                        )}
                        {comparisonEvaluatorScore !== null && (
                          <span
                            className="ml-2 font-bold"
                            style={{ color: COLORS.comparison }}
                          >
                            ({comparisonEvaluatorScore.toFixed(1)})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Selected Evaluator View
  const SelectedEvaluatorView: React.FC = () => {
    // Drinks for this evaluator
    const evaluatorDrinksArr = data
      .filter(
        (item) =>
          item.Evaluator === selectedEvaluator &&
          (selectedGroup === '' || item.Date === selectedGroup),
      )
      .map((item) => item.Drink);

    const uniqueDrinks = [...new Set(evaluatorDrinksArr)];

    // Common drinks between primary and comparison evaluators
    const commonDrinks = selectedComparisonEvaluator
      ? getCommonDrinks(
          selectedEvaluator,
          selectedComparisonEvaluator,
          selectedGroup || null,
        )
      : [];

    if (uniqueDrinks.length === 0) {
      return (
        <div
          className="mb-8 text-center p-6"
          style={{ color: COLORS.secondary }}
        >
          No evaluations found for {selectedEvaluator}
          {selectedGroup ? ` on ${selectedGroup}` : ''}.
        </div>
      );
    }

    return (
      <div className="mb-8">
        <h2
          className="text-xl font-semibold mb-4"
          style={{ color: COLORS.secondary }}
        >
          {selectedEvaluator}'s Evaluations{' '}
          {selectedGroup ? `on ${selectedGroup}` : ''}
        </h2>

        {/* Single legend for all evaluator charts */}
        <div className="flex justify-center mb-4 space-x-6">
          <div className="flex items-center">
            <div
              className="w-4 h-1 border-t-2 border-dashed mr-1"
              style={{ borderColor: COLORS.accent }}
            ></div>
            <span className="text-xs" style={{ color: COLORS.accent }}>
              {selectedEvaluator}
            </span>
          </div>
          {selectedComparisonEvaluator && (
            <div className="flex items-center">
              <div
                className="w-4 h-1 border-t-2 border-dashed mr-1"
                style={{ borderColor: COLORS.comparison }}
              ></div>
              <span className="text-xs" style={{ color: COLORS.comparison }}>
                {selectedComparisonEvaluator}
              </span>
            </div>
          )}
          <div className="flex items-center">
            <div
              className="w-4 h-1 mr-1"
              style={{ backgroundColor: COLORS.tertiary }}
            ></div>
            <span className="text-xs" style={{ color: COLORS.tertiary }}>
              Average
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
          {uniqueDrinks.map((drink) => {
            const evaluatorDataObj = [...data]
              .filter(
                (item) =>
                  item.Drink === drink &&
                  item.Evaluator === selectedEvaluator &&
                  (selectedGroup === '' || item.Date === selectedGroup),
              )
              .at(-1);
            if (!evaluatorDataObj) return null;

            const evaluatorRating = {
              taste: evaluatorDataObj.Taste || 0,
              balance: evaluatorDataObj.Balance || 0,
              aroma: evaluatorDataObj.Aroma || 0,
              presentation: evaluatorDataObj.Presentation || 0,
              texture: evaluatorDataObj.Texture || 0,
              avgScore: evaluatorDataObj['Avg Score'] || 0,
            };

            const averageRating = getDrinkCriteriaAverages(drink, null);

            // Get comparison evaluator's rating if available
            let comparisonEvaluatorRating = null;
            if (selectedComparisonEvaluator && commonDrinks.includes(drink)) {
              comparisonEvaluatorRating = getEvaluatorRating(
                drink,
                selectedComparisonEvaluator,
                selectedGroup || null,
              );
            }

            return (
              <div
                key={drink}
                className="p-4 rounded-lg shadow-md border border-gray-200 w-full"
                style={{
                  backgroundColor: COLORS.background,
                  width: '375px', // iPhone width (increased from 320px)
                  maxWidth: '100%',
                }}
              >
                <h3
                  className="text-lg font-medium text-center mb-2 h-12 overflow-hidden"
                  style={{ color: COLORS.secondary }}
                >
                  {drink}
                </h3>
                <PentagonChart
                  criteria={evaluatorRating}
                  comparisonCriteria={averageRating}
                  comparisonEvaluatorCriteria={comparisonEvaluatorRating}
                  drinkName={drink}
                  isEvaluator={true}
                  showLegend={false} // Don't show legend in the chart component
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ScatterPlot Chart Component - Completely reworked for proper grouping
  const ScatterPlotChart: React.FC = () => {
    // Get unique x-axis categories based on selection state
    const [xLabels, setXLabels] = useState<string[]>([]);

    // Track if we need to show a legend (when there are highlighted points)
    const showScatterLegend = selectedDrink || selectedEvaluator;

    // Use useEffect to properly set x-axis labels when relevant states change
    useEffect(() => {
      if (loading || data.length === 0) return;

      // Apply group filtering if needed
      let filteredData = selectedGroup
        ? data.filter((d) => d.Date === selectedGroup)
        : data;

      // Determine unique x-axis labels
      let uniqueLabels: string[];

      if (selectedEvaluator) {
        // When evaluator is selected, x-axis is drinks
        uniqueLabels = [
          ...new Set(
            filteredData
              .filter((d) => d.Evaluator === selectedEvaluator)
              .map((d) => d.Drink),
          ),
        ].sort();
      } else if (selectedDrink) {
        // When only drink is selected (no evaluator), x-axis should be evaluators who rated this drink
        uniqueLabels = [
          ...new Set(
            filteredData
              .filter((d) => d.Drink === selectedDrink)
              .map((d) => d.Evaluator),
          ),
        ].sort();
      } else {
        // Otherwise, x-axis is all evaluators
        uniqueLabels = [
          ...new Set(filteredData.map((d) => d.Evaluator)),
        ].sort();
      }

      setXLabels(uniqueLabels);
    }, [data, selectedGroup, selectedEvaluator, selectedDrink, loading]);

    const renderCustomTick = ({ x, y, payload }: any) => {
      const index = payload.value;
      if (index < 0 || index >= xLabels.length) {
        return <g />; // Return empty SVG group instead of null
      }

      const label = xLabels[index];
      const isBold = selectedEvaluator
        ? label === selectedDrink
        : label === selectedEvaluator;

      return (
        <text
          x={x}
          y={y}
          dy={8}
          textAnchor="end"
          fill={COLORS.secondary}
          fontSize={8}
          fontWeight={isBold ? 'bold' : 'normal'}
          transform={`rotate(-45, ${x}, ${y})`}
        >
          {label}
        </text>
      );
    };

    // Transform data for grouped scatter plot
    const getScatterData = () => {
      if (loading || data.length === 0 || xLabels.length === 0) {
        return [];
      }

      // Filter data based on current selections
      let filteredData = data;
      if (selectedGroup) {
        filteredData = filteredData.filter((d) => d.Date === selectedGroup);
      }

      // If only drink is selected (no evaluator), filter to only show evaluators who rated this drink
      if (selectedDrink && !selectedEvaluator) {
        // Get list of evaluators who rated this drink
        const evaluatorsWhoRatedDrink = [
          ...new Set(
            filteredData
              .filter((d) => d.Drink === selectedDrink)
              .map((d) => d.Evaluator),
          ),
        ];

        // Filter to only show ratings from these evaluators
        filteredData = filteredData.filter((d) =>
          evaluatorsWhoRatedDrink.includes(d.Evaluator),
        );
      }

      // Map data to chart format
      const mappedData = filteredData
        .map((item) => {
          const xLabel = selectedEvaluator ? item.Drink : item.Evaluator;
          const xIndex = xLabels.indexOf(xLabel);

          // Only include points where we have the x-label in our list
          if (xIndex !== -1) {
            return {
              // Use numeric index for x position to ensure proper grouping
              x: xIndex,
              // Store the actual label for tooltip
              xLabel,
              y: item['Avg Score'] || 0,
              evaluator: item.Evaluator,
              drink: item.Drink,
              isSelectedEvaluator: item.Evaluator === selectedEvaluator,
              isSelectedDrink: item.Drink === selectedDrink,
              // Add a priority value for sorting
              priority:
                item.Evaluator === selectedEvaluator ||
                item.Drink === selectedDrink
                  ? 1
                  : 0,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      // Sort the data so that highlighted points (priority 1) come last and will render on top
      return mappedData
        .filter(
          (item): item is typeof item & { priority: number } => item !== null,
        )
        .sort((a, b) => a.priority - b.priority);
    };

    const scatterData = getScatterData();

    if (loading || scatterData.length === 0) {
      return null;
    }

    return (
      <div className="mt-8">
        <h2
          className="text-xl font-semibold mb-4"
          style={{ color: COLORS.secondary }}
        >
          Rating Scatter
        </h2>
        <div className="w-full h-96">
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 20 }}>
              {/* Removed CartesianGrid to get rid of dotted tick lines */}
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, xLabels.length - 1]}
                tick={renderCustomTick}
                tickFormatter={(value) => {
                  const index = Math.round(value);
                  return index >= 0 && index < xLabels.length
                    ? xLabels[index]
                    : '';
                }}
                ticks={xLabels.map((_, i) => i)}
                angle={-45}
                height={120}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={[0, 10]}
                name="Score"
                tick={{ fill: COLORS.secondary }}
                label={{
                  value: 'Score',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: COLORS.secondary },
                }}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="p-2 bg-white border border-gray-200 shadow-md rounded">
                        <p className="font-medium">{`${data.evaluator}: ${data.drink}`}</p>
                        <p>{`Score: ${Number(data.y).toFixed(1)}`}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Scatter
                name="score"
                data={scatterData}
                fill={COLORS.tertiary}
                shape={(props: any) => {
                  const { cx, cy, payload } = props as {
                    cx: number;
                    cy: number;
                    payload: any;
                  };

                  // Point size logic - default 5, only larger (7) when both drink and evaluator selected
                  let size = 5; // default size is always 5

                  // Only change size when both drink and evaluator are selected AND it's the selected drink
                  if (
                    selectedDrink &&
                    selectedEvaluator &&
                    payload.isSelectedDrink &&
                    payload.isSelectedEvaluator
                  ) {
                    size = 7;
                  }

                  // Color logic
                  let color = COLORS.tertiary; // default color (blue)
                  let opacity = 1;

                  // Check if we need to highlight any dots
                  const hasHighlightedDots =
                    selectedEvaluator ||
                    selectedDrink ||
                    selectedComparisonEvaluator;

                  // When comparison evaluator is selected, use comparison color for their evaluations
                  if (
                    selectedComparisonEvaluator &&
                    payload.evaluator === selectedComparisonEvaluator
                  ) {
                    color = COLORS.comparison;
                    opacity = 1;
                  }
                  // When evaluator is selected, use accent color for that evaluator's evaluations
                  else if (selectedEvaluator && payload.isSelectedEvaluator) {
                    color = COLORS.accent;
                    opacity = 1;
                  }
                  // When only drink is selected (no evaluator selected), use accent for that drink
                  else if (
                    !selectedEvaluator &&
                    selectedDrink &&
                    payload.isSelectedDrink
                  ) {
                    color = COLORS.accent;
                    opacity = 1;
                  }
                  // If we have highlighted dots but this specific dot isn't highlighted, reduce its opacity
                  else if (hasHighlightedDots) {
                    opacity = 0.5; // Lower opacity for non-highlighted dots
                  }

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={size}
                      fill={color}
                      fillOpacity={opacity}
                      stroke="none"
                    />
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>

          {/* Scatter Plot Legend - Moved right under the chart */}
          {showScatterLegend && (
            <div className="flex justify-center mt-0 mb-6 space-x-6">
              <div className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-2"
                  style={{ backgroundColor: COLORS.tertiary }}
                ></div>
                <span className="text-sm" style={{ color: COLORS.secondary }}>
                  Other Ratings
                </span>
              </div>
              <div className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-2"
                  style={{ backgroundColor: COLORS.accent }}
                ></div>
                <span className="text-sm" style={{ color: COLORS.secondary }}>
                  {selectedEvaluator
                    ? `${selectedEvaluator}'s Ratings`
                    : selectedDrink
                    ? `${selectedDrink} Ratings`
                    : ''}
                </span>
              </div>
              {selectedComparisonEvaluator && (
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: COLORS.comparison }}
                  ></div>
                  <span className="text-sm" style={{ color: COLORS.secondary }}>
                    {`${selectedComparisonEvaluator}'s Ratings`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Now that data is loaded, if still loading, show a spinner
  if (loading) {
    return (
      <div className="p-8 text-center" style={{ color: COLORS.secondary }}>
        Loading cocktail data...
      </div>
    );
  }

  // Otherwise, normal rendering
  const filteredOptions = getFilteredOptions();
  const top5Drinks = getTop5Drinks();

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      style={{ backgroundColor: COLORS.background }}
    >
      <h1
        className="text-4xl font-bold mb-8 text-center"
        style={{ color: COLORS.secondary }}
      >
        Unravel Cocktail Tasting
      </h1>

      {/* Modern compact filters - Fixed to top on scroll */}
      <div
        className="sticky top-0 z-10 mb-8 p-4 rounded-lg border border-gray-200 shadow-sm"
        style={{
          backgroundColor: COLORS.background,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="space-y-4">
          <div className="flex space-x-4 w-full">
            <div className="w-1/3">
              <select
                className="w-full p-2 text-sm border rounded-md"
                style={{
                  borderColor: COLORS.secondary,
                  color: COLORS.secondary,
                  backgroundColor: COLORS.background,
                }}
                value={selectedGroup}
                onChange={(e) => {
                  const newGroupValue = e.target.value;

                  // Check if we need to reset selected drink/evaluator when changing groups
                  if (newGroupValue) {
                    // Only reset if the selected drink/evaluator isn't in the new group
                    const shouldKeepDrink = selectedDrink
                      ? isDrinkInGroup(selectedDrink, newGroupValue)
                      : false;

                    const shouldKeepEvaluator = selectedEvaluator
                      ? isEvaluatorInGroup(selectedEvaluator, newGroupValue)
                      : false;

                    setSelectedGroup(newGroupValue);

                    if (!shouldKeepDrink) {
                      setSelectedDrink('');
                    }

                    if (!shouldKeepEvaluator) {
                      setSelectedEvaluator('');
                      setSelectedComparisonEvaluator('');
                    }
                  } else {
                    // If clearing group filter, keep other selections
                    setSelectedGroup('');
                  }
                }}
              >
                <option value="">All Evaluator Groups</option>
                {evaluatorGroups.map((group) => (
                  <option key={group.date} value={group.date}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-1/3">
              <select
                className="w-full p-2 text-sm border rounded-md"
                style={{
                  borderColor: COLORS.secondary,
                  color: COLORS.secondary,
                  backgroundColor: COLORS.background,
                }}
                value={selectedDrink}
                onChange={(e) => {
                  setSelectedDrink(e.target.value);
                  if (e.target.value === '') {
                    // If no drink is selected and there's a comparison evaluator, keep it
                  } else {
                    // Keep comparison evaluator only if it has evaluated this drink
                    if (selectedComparisonEvaluator) {
                      const hasEvaluated = data.some(
                        (item) =>
                          item.Evaluator === selectedComparisonEvaluator &&
                          item.Drink === e.target.value,
                      );
                      if (!hasEvaluated) {
                        setSelectedComparisonEvaluator('');
                      }
                    }
                  }
                }}
              >
                <option value="">All Drinks</option>
                {filteredOptions.drinkOptions.map((drink) => (
                  <option key={drink} value={drink}>
                    {drink}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-1/3">
              <select
                className="w-full p-2 text-sm border rounded-md"
                style={{
                  borderColor: COLORS.secondary,
                  color: COLORS.secondary,
                  backgroundColor: COLORS.background,
                }}
                value={selectedEvaluator}
                onChange={(e) => {
                  setSelectedEvaluator(e.target.value);
                  setSelectedComparisonEvaluator(''); // Reset comparison when primary changes
                }}
              >
                <option value="">All Evaluators</option>
                {filteredOptions.evaluatorOptions.map((evaluator) => (
                  <option key={evaluator} value={evaluator}>
                    {evaluator}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Comparison Evaluator Dropdown - Only show when a primary evaluator is selected */}
          {selectedEvaluator && (
            <div className="flex space-x-4 w-full">
              <div className="w-1/3">
                {/* Empty placeholder for alignment */}
              </div>
              <div className="w-1/3">
                {/* Empty placeholder for alignment */}
              </div>
              <div className="w-1/3">
                <select
                  className="w-full p-2 text-sm border rounded-md"
                  style={{
                    borderColor: COLORS.comparison,
                    color: COLORS.secondary,
                    backgroundColor: COLORS.background,
                  }}
                  value={selectedComparisonEvaluator}
                  onChange={(e) =>
                    setSelectedComparisonEvaluator(e.target.value)
                  }
                >
                  <option value="">Compare with...</option>
                  {filteredOptions.comparisonEvaluatorOptions.map(
                    (evaluator) => (
                      <option key={evaluator} value={evaluator}>
                        {evaluator}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Clear Filters button - only show if at least one filter is active */}
        {(selectedGroup !== '' ||
          selectedDrink !== '' ||
          selectedEvaluator !== '' ||
          selectedComparisonEvaluator !== '') && (
          <div className="flex justify-end">
            <button
              className="px-4 py-1 text-sm rounded-md transition-colors"
              style={{
                backgroundColor: COLORS.secondary,
                color: COLORS.background,
              }}
              onClick={() => {
                setSelectedGroup('');
                setSelectedDrink('');
                setSelectedEvaluator('');
                setSelectedComparisonEvaluator('');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Top 5 Drinks Section */}
      {!selectedDrink && !selectedEvaluator && !selectedGroup && (
        <div className="mb-8">
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: COLORS.secondary }}
          >
            Top 5 Cocktails
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 justify-items-center">
            {top5Drinks.map((d) => {
              const criteria = getDrinkCriteriaAverages(d.drink);
              return (
                <div
                  key={d.drink}
                  className="p-4 rounded-lg shadow-md border border-gray-200 w-full"
                  style={{
                    backgroundColor: COLORS.background,
                    width: '375px', // iPhone width (increased from 320px)
                    maxWidth: '100%',
                  }}
                >
                  <h3
                    className="text-lg font-medium text-center mb-2 h-12 overflow-hidden"
                    style={{ color: COLORS.secondary }}
                  >
                    {d.drink}
                  </h3>
                  <PentagonChart
                    criteria={criteria}
                    drinkName={d.drink}
                    showLegend={false}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Drink View */}
      {selectedDrink && !selectedEvaluator && (
        <div className="mb-8">
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: COLORS.secondary }}
          >
            {selectedDrink}
          </h2>
          <div
            className="p-6 rounded-lg shadow-md max-w-md mx-auto border border-gray-200"
            style={{
              backgroundColor: COLORS.background,
              minWidth: '375px', // iPhone width (increased from 320px)
            }}
          >
            {(() => {
              // Calculate average ratings for this drink
              const criteria = getDrinkCriteriaAverages(selectedDrink);

              if (!criteria) {
                return (
                  <div
                    className="text-center p-4"
                    style={{ color: COLORS.secondary }}
                  >
                    No evaluations found for {selectedDrink}.
                  </div>
                );
              }

              return (
                <PentagonChart
                  criteria={criteria}
                  drinkName={selectedDrink}
                  showLegend={true}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* Selected Evaluator View */}
      {selectedEvaluator && !selectedDrink && <SelectedEvaluatorView />}

      {/* Selected Drink and Evaluator View */}
      {selectedDrink && selectedEvaluator && (
        <div className="mb-8">
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: COLORS.secondary }}
          >
            {selectedEvaluator}'s Evaluation of {selectedDrink}
          </h2>
          <div
            className="p-6 rounded-lg shadow-md max-w-md mx-auto border border-gray-200"
            style={{
              backgroundColor: COLORS.background,
              minWidth: '375px', // iPhone width (increased from 320px)
            }}
          >
            {(() => {
              const evaluatorDataObj = [...data]
                .filter(
                  (item) =>
                    item.Drink === selectedDrink &&
                    item.Evaluator === selectedEvaluator &&
                    (selectedGroup === '' || item.Date === selectedGroup),
                )
                .at(-1);
              if (!evaluatorDataObj) {
                return (
                  <div
                    className="text-center p-4"
                    style={{ color: COLORS.secondary }}
                  >
                    No evaluation found for {selectedEvaluator} on{' '}
                    {selectedDrink}{' '}
                    {selectedGroup ? `from ${selectedGroup}` : ''}.
                  </div>
                );
              }

              const evaluatorRating = {
                taste: evaluatorDataObj.Taste || 0,
                balance: evaluatorDataObj.Balance || 0,
                aroma: evaluatorDataObj.Aroma || 0,
                presentation: evaluatorDataObj.Presentation || 0,
                texture: evaluatorDataObj.Texture || 0,
                avgScore: evaluatorDataObj['Avg Score'] || 0,
              };

              const averageRating = getDrinkCriteriaAverages(
                selectedDrink,
                null,
              );

              // Get comparison evaluator's rating if available
              let comparisonEvaluatorRating = null;
              if (selectedComparisonEvaluator) {
                comparisonEvaluatorRating = getEvaluatorRating(
                  selectedDrink,
                  selectedComparisonEvaluator,
                  selectedGroup || null,
                );
              }

              return (
                <>
                  <PentagonChart
                    criteria={evaluatorRating}
                    comparisonCriteria={averageRating}
                    comparisonEvaluatorCriteria={comparisonEvaluatorRating}
                    drinkName={selectedDrink}
                    isEvaluator={true}
                  />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Group View - MODIFIED to not filter pentagon data */}
      {selectedGroup && !selectedDrink && !selectedEvaluator && (
        <div className="mb-8">
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: COLORS.secondary }}
          >
            Drinks evaluated by{' '}
            {evaluatorGroups.find((g) => g.date === selectedGroup)?.label}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {/* Get drinks evaluated by this group but use overall averages for the pentagons */}
            {getDrinksByGroup(selectedGroup).map((drink) => {
              // Use all data for calculating criteria, not just group data
              const criteria = getDrinkCriteriaAverages(drink, null);

              return (
                <div
                  key={drink}
                  className="p-4 rounded-lg shadow-md border border-gray-200 w-full"
                  style={{
                    backgroundColor: COLORS.background,
                    width: '375px', // iPhone width (increased from 320px)
                    maxWidth: '100%',
                  }}
                >
                  <h3
                    className="text-lg font-medium text-center mb-2 h-12 overflow-hidden"
                    style={{ color: COLORS.secondary }}
                  >
                    {drink}
                  </h3>
                  <PentagonChart
                    criteria={criteria}
                    drinkName={drink}
                    showLegend={false}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scatter Plot: Evaluator Scores */}
      <ScatterPlotChart />

      {/* Always show the bar chart */}
      <ScoreBarChart />
    </div>
  );
};

export default CocktailDashboard;
